import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Container, Typography, Link as MuiLink } from '@mui/material';

const TermsAndConditions = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="md" sx={{ my: 4, px: 2 }}>
      {/* Back Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 3 }}>
        <Button variant="contained" onClick={() => navigate(-1)}>
          ← Back
        </Button>
      </Box>

      {/* Title and Last Updated */}
      <Typography variant="h3" component="h1" gutterBottom>
        Terms and Conditions
      </Typography>
      <Typography variant="subtitle1" gutterBottom>
        <strong>Last Updated:</strong> 31.03.2025
      </Typography>

      {/* Introduction */}
      <Typography variant="h5" component="h2" sx={{ mt: 8, mb: 2 }}>
        Introduction
      </Typography>
      <Typography variant="body1">
        Welcome to Medicheck, owned and operated by Vision Grid. These Terms and Conditions ("Terms") govern your access to and use of our subscription-based online pharmacy information platform available at{' '}
        <MuiLink href="http://www.medicheck.lk/" target="_blank" rel="noopener noreferrer">
          www.medicheck.lk
        </MuiLink>. By using Medicheck, you agree to comply with these Terms.
      </Typography>

      {/* Use of the Website */}
      <Typography variant="h5" component="h2" sx={{ mt: 8, mb: 2 }}>
        1. Use of the Website
      </Typography>
      <Typography variant="body1" sx={{ mb: 1 }}>
        a) You must be at least 18 years old to use Medicheck.
      </Typography>
      <Typography variant="body1" sx={{ mb: 1 }}>
        b) You must provide accurate, complete, and current information when registering for an account.
      </Typography>
      <Typography variant="body1" sx={{ mb: 1 }}>
        c) You agree to provide accurate and current information during the sign-up and subscription process.
      </Typography>
      <Typography variant="body1" sx={{ mb: 1 }}>
        d) You are solely responsible for maintaining the confidentiality and security of your account details and for all activities under your account.
      </Typography>
      <Typography variant="body1" sx={{ mb: 1 }}>
        e) Medicheck is not intended to provide medical advice. It is designed solely as a search engine and guidance tool for pharmacists to assist in identifying medicines, similar medicines, brands, and side effects. Like any tool, it may occasionally produce errors. It remains the user's responsibility to verify information independently if uncertain.
      </Typography>
      <Typography variant="body1" sx={{ mb: 1 }}>
        f) You may not use our website for any unlawful or unauthorized purposes.
      </Typography>
      <Typography variant="body1" sx={{ mb: 1 }}>
        g) Medicheck's service is intended for professional use by pharmacists and pharmacy business employees under the supervision of a pharmacist.
      </Typography>
      <Typography variant="body1" sx={{ mb: 1 }}>
        h) Unauthorized sharing of your subscription access or attempting to bypass the subscription to use our service is prohibited and may result in account termination and banning of the pharmacy from using our service.
      </Typography>
      <Typography variant="body1" sx={{ mb: 1 }}>
        i) You must not reproduce, distribute, sell, or exploit any content or data provided by Medicheck for commercial purposes without our prior written consent.
      </Typography>

      {/* Subscription and Payment */}
      <Typography variant="h5" component="h2" sx={{ mt: 8, mb: 2 }}>
        2. Subscription and Payment
      </Typography>
      <Typography variant="body1" sx={{ mb: 1 }}>
        a) Medicheck offers monthly subscription-based access to pharmaceutical information, including medicine details, brand alternatives, and side-effect information tailored for pharmacists and pharmacy businesses.
      </Typography>
      <Typography variant="body1" sx={{ mb: 1 }}>
        b) You authorize automatic monthly billing to your selected payment method. Subscription charges will recur monthly until you cancel and/or delete your account.
      </Typography>
      <Typography variant="body1" sx={{ mb: 1 }}>
        c) We reserve the right to cancel or terminate account access if we suspect exploitation or misuse of our system by the user.
      </Typography>
      <Typography variant="body1" sx={{ mb: 1 }}>
        d) We use trusted third-party payment processors to handle your payment information securely. We do not store or have access to your full payment details.
      </Typography>

      {/* Refunds */}
      <Typography variant="h5" component="h2" sx={{ mt: 8, mb: 2 }}>
        3. Refunds
      </Typography>
      <Typography variant="body1">
        Our Refund Policy governs the process and conditions under which a refund is valid. Please refer to our detailed Refund Policy available on our website.
      </Typography>

      {/* Intellectual Property */}
      <Typography variant="h5" component="h2" sx={{ mt: 8, mb: 2 }}>
        4. Intellectual Property
      </Typography>
      <Typography variant="body1" sx={{ mb: 1 }}>
        a) All content and materials on Medicheck, including but not limited to databases, text, images, and software, are owned by Vision Grid or its licensors and protected by applicable intellectual property laws.
      </Typography>
      <Typography variant="body1" sx={{ mb: 1 }}>
        b) You are granted a limited, non-exclusive, non-transferable license to access and use Medicheck's content for your professional practice only.
      </Typography>

      {/* Limitation of Liability */}
      <Typography variant="h5" component="h2" sx={{ mt: 8, mb: 2 }}>
        5. Limitation of Liability
      </Typography>
      <Typography variant="body1" sx={{ mb: 1 }}>
        a) Medicheck is provided on an "as is" basis. Vision Grid makes no warranties, either express or implied, about the accuracy, completeness, or reliability of the content provided.
      </Typography>
      <Typography variant="body1" sx={{ mb: 1 }}>
        b) Vision Grid is not liable for any direct, indirect, incidental, special, or consequential damages resulting from your use of the Medicheck service.
      </Typography>
      <Typography variant="body1" sx={{ mb: 1 }}>
        c) In no event shall Vision Grid’s total liability exceed the amount you have paid in subscription fees within the three months preceding any claim.
      </Typography>

      {/* Changes to Terms */}
      <Typography variant="h5" component="h2" sx={{ mt: 8, mb: 2 }}>
        6. Changes to Terms
      </Typography>
      <Typography variant="body1" sx={{ mb: 1 }}>
        a) We reserve the right to modify these Terms at any time without prior notice. Any changes will be posted on Medicheck's website with the revised "Last Updated" date.
      </Typography>
      <Typography variant="body1" sx={{ mb: 1 }}>
        b) Your continued use of Medicheck after any changes indicates your acceptance of the revised Terms.
      </Typography>

      {/* Contact Information */}
      <Typography variant="h5" component="h2" sx={{ mt: 8, mb: 2 }}>
        7. Contact Information
      </Typography>
      <Typography variant="body1">
        If you have questions or concerns regarding these Terms, please contact us at:
      </Typography>
      <Typography variant="body1">
        Email: info@medicheck.lk
      </Typography>
    </Container>
  );
};

export default TermsAndConditions;
